
import { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Shield, Clock, Eye } from "lucide-react";
import { useNotifications } from "@/hooks/useNotifications";

interface SecurityAlert {
  id: string;
  type: 'denied_entry' | 'suspicious_activity' | 'expired_verification' | 'banned_patron';
  venue: string;
  patron: string;
  timestamp: Date;
  details: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

export const SecurityAlerts = () => {
  const { addNotification } = useNotifications();

  // Mock security alerts for demo
  const alerts: SecurityAlert[] = [
    {
      id: '1',
      type: 'denied_entry',
      venue: 'Club Infinity',
      patron: 'John Doe',
      timestamp: new Date(Date.now() - 5 * 60 * 1000),
      details: 'ID verification expired 3 days ago',
      severity: 'high'
    },
    {
      id: '2',
      type: 'suspicious_activity',
      venue: 'The Basement',
      patron: 'Unknown',
      timestamp: new Date(Date.now() - 15 * 60 * 1000),
      details: 'Multiple failed QR scan attempts',
      severity: 'medium'
    },
    {
      id: '3',
      type: 'banned_patron',
      venue: 'Neon Nights',
      patron: 'Alex Wilson',
      timestamp: new Date(Date.now() - 30 * 60 * 1000),
      details: 'Attempted entry while banned until 2024-12-31',
      severity: 'critical'
    }
  ];

  // Simulate real-time alerts
  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() > 0.9) { // 10% chance every 30 seconds
        const alertTypes = ['denied_entry', 'suspicious_activity', 'expired_verification'];
        const venues = ['Club Infinity', 'The Basement', 'Neon Nights', 'Underground'];
        const patrons = ['John Doe', 'Jane Smith', 'Mike Johnson', 'Sarah Lee'];
        
        const type = alertTypes[Math.floor(Math.random() * alertTypes.length)] as any;
        const venue = venues[Math.floor(Math.random() * venues.length)];
        const patron = patrons[Math.floor(Math.random() * patrons.length)];
        
        addNotification({
          type: 'security_alert',
          title: 'Security Alert',
          message: `${type.replace('_', ' ')} at ${venue} - ${patron}`,
          priority: Math.random() > 0.7 ? 'high' : 'medium',
          metadata: { venue, patron, alertType: type }
        });
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [addNotification]);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'denied_entry': return <Shield className="h-4 w-4 text-red-500" />;
      case 'suspicious_activity': return <Eye className="h-4 w-4 text-orange-500" />;
      case 'expired_verification': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'banned_patron': return <AlertTriangle className="h-4 w-4 text-red-600" />;
      default: return <AlertTriangle className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatTimeAgo = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <Card className="glass-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          Security Alerts
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className="p-4 border rounded-lg bg-background/50 hover:bg-background/70 transition-colors"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                {getAlertIcon(alert.type)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-medium text-sm">
                      {alert.type.replace('_', ' ').toUpperCase()}
                    </h4>
                    <Badge 
                      variant="secondary" 
                      className={`text-xs ${getSeverityColor(alert.severity)}`}
                    >
                      {alert.severity}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-1">
                    <strong>{alert.venue}</strong> • {alert.patron}
                  </p>
                  <p className="text-sm text-muted-foreground mb-2">
                    {alert.details}
                  </p>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {formatTimeAgo(alert.timestamp)}
                  </div>
                </div>
              </div>
              <Button variant="outline" size="sm">
                View Details
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
